import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ServiceProvider, SearchFilters, MapBounds } from '@/types';

// Transform database row (snake_case) to TypeScript (camelCase)
function transformProvider(row: Record<string, unknown>): ServiceProvider {
  return {
    id: row.id as string,
    userId: row.user_id as string | null,
    organizationId: row.organization_id as string | null,
    businessName: row.business_name as string,
    slug: row.slug as string,
    description: row.description as string | null,
    phone: row.phone as string | null,
    email: row.email as string | null,
    website: row.website as string | null,
    address: row.address as string | null,
    city: row.city as string | null,
    state: row.state as string | null,
    zipCode: row.zip_code as string | null,
    latitude: row.latitude as number | null,
    longitude: row.longitude as number | null,
    serviceTypes: (row.service_types || []) as ServiceProvider['serviceTypes'],
    specialties: (row.specialties as string[]) || [],
    serviceAreaMiles: (row.service_area_miles as number) || 25,
    isVerified: row.is_verified as boolean,
    isClaimed: row.is_claimed as boolean,
    logoUrl: row.logo_url as string | null,
    coverImageUrl: row.cover_image_url as string | null,
    galleryUrls: (row.gallery_urls as string[]) || [],
    hours: (row.hours as ServiceProvider['hours']) || {},
    certifications: (row.certifications as string[]) || [],
    yearsInBusiness: row.years_in_business as number | null,
    averageRating: null, // Will be computed
    reviewCount: 0, // Will be computed
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

async function fetchProviders(
  filters: SearchFilters,
  bounds?: MapBounds
): Promise<ServiceProvider[]> {
  let query = supabase.from('service_providers').select('*');

  // Filter by service types using overlap (&&) operator
  if (filters.serviceTypes && filters.serviceTypes.length > 0) {
    query = query.overlaps('service_types', filters.serviceTypes);
  }

  // Filter by verified
  if (filters.isVerified) {
    query = query.eq('is_verified', true);
  }

  // Filter by map bounds
  if (bounds) {
    query = query
      .gte('latitude', bounds.south)
      .lte('latitude', bounds.north)
      .gte('longitude', bounds.west)
      .lte('longitude', bounds.east);
  }

  // Filter by location (city/state search)
  if (filters.location) {
    const locationLower = filters.location.toLowerCase();
    query = query.or(`city.ilike.%${locationLower}%,state.ilike.%${locationLower}%`);
  }

  // Filter by text query (business name)
  if (filters.query) {
    query = query.ilike('business_name', `%${filters.query}%`);
  }

  const { data, error } = await query.order('is_verified', { ascending: false });

  if (error) {
    console.error('Error fetching providers:', error);
    throw error;
  }

  return (data || []).map(transformProvider);
}

export function useProviders(filters: SearchFilters, bounds?: MapBounds) {
  return useQuery({
    queryKey: ['providers', filters, bounds],
    queryFn: () => fetchProviders(filters, bounds),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
